import { useEffect, useRef, useState } from "react";

import GridViewIcon from "@mui/icons-material/GridView";
import MapIcon from "@mui/icons-material/Map";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Alert,
  CircularProgress,
  Tooltip,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { PublicBulletinResource } from "@rubi/api-generated";
import { GoogleMapsProvider } from "@rubi/ui";
import { toast } from "@rubi/ui";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";

import { usePublicAlertsInfiniteQuery } from "@/api/loadAlerts";

import PublicAlertMap from "./PublicAlertMap";
import PublicBulletinCard from "./PublicBulletinCard";
import "@/components/alerts/alerts.scss";
interface IPublicAlertListProps {
  groupId?: string;
}

// Rubicon Logo watermark component
const RubiconLogo = () => (
  <div
    className="logo"
    style={{
      position: "absolute",
      top: "15px",
      right: "15px",
      zIndex: 1000,
    }}
  >
    <b style={{ fontSize: 12, color: "grey" }}>POWERED BY</b>
    <img
      src="/Rubicon Logo.png"
      alt="Rubicon logo"
      style={{ height: "28px", marginTop: "-8px" }}
      onClick={() => window.open("https://tryRubicon.com", "_blank")}
    />
  </div>
);

export const PublicAlertList = ({
  groupId: propsGroupId,
}: IPublicAlertListProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { groupId: routeGroupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();

  // 优先使用路由参数中的groupId，如果没有则使用props中的groupId
  const groupId = routeGroupId || propsGroupId;

  // 获取当前视图
  const view = searchParams.get("view") || "card";

  const [currentItems, setCurrentItems] = useState<PublicBulletinResource[]>(
    [],
  );
  const [totalAlerts, setTotalAlerts] = useState(0);

  const topPageRef = useRef<HTMLDivElement | null>(null);
  const bottomPageRef = useRef<HTMLDivElement>(null);

  // Regular bulletin list query
  const {
    data,
    error: alertsError,
    fetchNextPage,
    hasNextPage,
    isLoading,
  } = usePublicAlertsInfiniteQuery(25, groupId || "", null, false);

  const [isLoadingAlerts, setIsLoadingAlerts] = useState(isLoading);

  const handleViewChange = (newView: string) => {
    // if change to card view, clear the search params
    if (newView === "card") {
      setSearchParams((searchParams) => {
        searchParams.delete("view");
        searchParams.delete("bounds");
        searchParams.delete("occurrence_number");
        searchParams.delete("selectedAddressIndex");
        return searchParams;
      });
    } else {
      setSearchParams((searchParams) => {
        searchParams.set("view", newView);
        return searchParams;
      });
    }
  };

  const loadMore = () => {
    if (hasNextPage && currentItems.length < totalAlerts) {
      fetchNextPage();
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            fetchNextPage();
          }
        });
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      },
    );

    const currentAnchor = bottomPageRef.current;
    if (currentAnchor) {
      observer.observe(currentAnchor);
    }

    return () => {
      if (currentAnchor) {
        observer.unobserve(currentAnchor);
      }
    };
  }, [bottomPageRef.current]);

  useEffect(() => {
    if (data) {
      const items = data.pages.flatMap((page) => page?.items ?? []);
      setCurrentItems(items);
      setTotalAlerts(data.pages[0]?.total_count ?? 0);
    }
  }, [data]);

  useEffect(() => {
    if (isLoadingAlerts && hasNextPage) {
      loadMore();
    } else if (isLoadingAlerts && !hasNextPage) {
      setIsLoadingAlerts(false);
    }
  }, [isLoadingAlerts, hasNextPage]);

  if (alertsError) {
    toast.error("Error fetching alerts");
  }

  const alertClickHandler = (alertItem: PublicBulletinResource) => {
    navigate(
      `/rubialert/public/group/${groupId}/bulletin?id=${alertItem.encrypted_id}`,
    );
  };

  const renderBulletinList = () => {
    return (
      <div
        className="alert-list mt-1 pt-3"
        style={{ position: "relative", width: "99%", margin: "0 auto" }}
      >
        <RubiconLogo />
        <div id="top-page-div" ref={topPageRef}></div>

        <div className="d-flex justify-content-end mb-3 mx-3">
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={(e, newView) => {
              if (newView !== null) {
                handleViewChange(newView);
              }
            }}
            aria-label="view mode"
            size="small"
          >
            <ToggleButton value="card" aria-label="card view">
              <Tooltip title="Card View">
                <GridViewIcon />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="map" aria-label="map view">
              <Tooltip title="Map View">
                <MapIcon />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </div>

        <Alert
          severity="warning"
          icon={
            <WarningAmberIcon fontSize="medium" sx={{ alignSelf: "center" }} />
          }
          sx={{ mb: 2, mx: "auto", width: "95%", fontWeight: 600 }}
        >
          If you see them in public, do not take pictures, approach, or follow.
          For your safety, do not confront or attempt to apprehend anyone.
        </Alert>

        <Typography
          variant="caption"
          display="block"
          align="center"
          sx={{ mb: 2, color: "text.secondary", width: "95%", mx: "auto" }}
        >
          Reminder that all persons identified are innocent until proven guilty
          in a court of law.
        </Typography>

        {view === "card" && (
          <div className="alert-items" style={{ width: "95%" }}>
            {currentItems?.length > 0 &&
              currentItems.map((bulletin, index) => (
                <PublicBulletinCard
                  key={bulletin.encrypted_id}
                  index={index}
                  alertItem={bulletin}
                  alertClickHandler={alertClickHandler}
                  className="mb-3"
                />
              ))}
          </div>
        )}

        {view === "map" && (
          <GoogleMapsProvider>
            <PublicAlertMap
              groupId={parseInt(groupId || "0")}
              isLoading={isLoading}
              alerts={currentItems}
              alertClickHandler={alertClickHandler}
            />
          </GoogleMapsProvider>
        )}

        <div ref={bottomPageRef} />
        {(isLoading || currentItems.length < totalAlerts) &&
          view === "card" && (
            <div
              className="loading-more"
              style={{
                padding: "16px 0",
              }}
            >
              <CircularProgress />
            </div>
          )}
      </div>
    );
  };

  return renderBulletinList();
};
